import styled from '@emotion/styled';

import { screen } from '@/components/common/breakpoints';
import {
  Heading,
  HeadingLevel,
  List,
  Paragraph,
} from '@/components/common/MarkUp';
import { PortableText } from '@/components/common/utils';
import { Section } from '@/components/layout/Section';
import { labelMedium } from '@/styles/typography';

import type { InformationText as SanityInformationText } from '@/sanity/sanity.types';

export interface InformationTextProps extends SanityInformationText { }

const normaliseHeading = (heading) => heading.replace(/[\s:]/g, '-');

const components = {
  block: ({ children }) => (
    <Paragraph size="subheading-sm">{children}</Paragraph>
  ),
  marks: {
    strong: ({ children }) => <b>{children}</b>,
  },
  list: ({ type, children }) => (
    <List size="subheading-sm" type={type === 'bullet' ? 'ul' : 'ol'}>
      {children}
    </List>
  ),
  listItem: ({ children }) => <ListItem>{children}</ListItem>,
};

const InformationText = ({ information }: InformationTextProps) => {
  const sideNavigationLinks = information.map((i) => ({
    heading: i.heading,
    href: `#${normaliseHeading(i.heading)}`,
  }));

  return (
    <Section
      backgroundColour="--background-subtle"
      className="information-text"
    >
      <InformationTextContainer>
        <SideNavigation>
          {sideNavigationLinks.map((link) => (
            <a key={link.href} href={link.href}>
              {link.heading}
            </a>
          ))}
        </SideNavigation>
        <MainContent>
          {information.map((info) => (
            <InfoBlock key={info._key} id={normaliseHeading(info.heading)}>
              <HeadingLevel>
                <Heading size="heading-md">{info.heading}</Heading>
                <PortableText value={info.text} components={components} />
              </HeadingLevel>
            </InfoBlock>
          ))}
        </MainContent>
      </InformationTextContainer>
    </Section>
  );
};

const InformationTextContainer = styled.div`
  display: flex;
  padding-top: var(--spacing-500);
`;
const SideNavigation = styled.div`
  display: none;

  a {
    text-decoration: none;
    ${labelMedium};
    color: var(--text-strong);
    margin-bottom: var(--spacing-500);
  }

  ${screen.md} {
    display: flex;
    flex-direction: column;
    max-width: 252px;
    margin-right: var(--spacing-800);
    position: sticky;
    top: 100px;
    height: max-content;
  }
`;
const MainContent = styled.div`
  width: 100%;
  ${screen.md} {
    width: 800px;
  }
`;
const InfoBlock = styled.div`
  /* spacer for anchor tag scrolling */
  &::before {
    content: ' ';
    display: block;
    height: 100px;
    margin-top: -100px;
    visibility: hidden;
  }

  h2 {
    margin: var(--spacing-500) 0 var(--spacing-300) 0;
    &:first-of-type {
      margin-top: 0;
    }
  }
  margin-bottom: var(--spacing-800);
  p {
    margin-bottom: var(--spacing-small);

    &:empty {
      height: 0px;
      &::before {
        content: ' ';
        white-space: pre;
      }
    }
  }
`;

const ListItem = styled.li`
  margin: var(--spacing-small) 0;
  margin-left: 1em;
`;

export { InformationText };
